export interface Book {
  title: string;
  author?: string;
  image?: string;
  badge?: string;
  tag?: string;
  description?: string;
  maincopy?: string;
  url?: string;
  barcode?: string;
  isBorrowed?: boolean;
}

export type BookSection = "literature" | "humanities_life" | "young";

export type BookHome = {
  recommended: Book[];
  newBooks: Book[];
  categories: Record<BookSection, Book[]>;
};

export const DEFAULT_BOOK_IMAGE = "/images/nobook.png";

export const categoryLabels: Record<BookSection, string> = {
  young: "어린이·청소년",
  literature: "소설·에세이",
  humanities_life: "인문·라이프",
};

export const categoryIds = Object.keys(categoryLabels) as BookSection[];

export function isBookSection(value: unknown): value is BookSection {
  return typeof value === "string" && categoryIds.includes(value as BookSection);
}

export const emptyBookHome = (): BookHome => ({
  recommended: [],
  newBooks: [],
  categories: { literature: [], humanities_life: [], young: [] },
});

export function normalizeBook(value: unknown): Book | null {
  if (!value || typeof value !== "object") return null;

  const book = value as Record<string, unknown>;
  const title = typeof book.title === "string" ? book.title.trim() : "";
  if (!title) return null;

  const textValue = (key: string) => {
    const field = book[key];
    return typeof field === "string" ? field.trim() || undefined : undefined;
  };
  return {
    title,
    author: textValue("author"),
    image: textValue("image"),
    badge: textValue("badge"),
    tag: textValue("tag"),
    description: textValue("description"),
    maincopy: textValue("maincopy"),
    url: textValue("url"),
    barcode: textValue("barcode"),
    isBorrowed: book.isBorrowed === true,
  };
}

export function normalizeBooks(value: unknown): Book[] {
  return Array.isArray(value) ? value.map(normalizeBook).filter((book: Book | null): book is Book => book !== null) : [];
}

export function normalizeBookHome(value: unknown): BookHome {
  if (!value || typeof value !== "object") return emptyBookHome();
  const result = value as Record<string, unknown>;
  const home = emptyBookHome();
  home.recommended = normalizeBooks(result.recommended);
  home.newBooks = normalizeBooks(result.newBooks ?? result.new_books);

  for (const section of categoryIds) home.categories[section] = normalizeBooks(result[section]);

  if (Array.isArray(result.sections)) {
    result.sections.forEach((section) => {
      if (!section || typeof section !== "object") return;
      const current = section as Record<string, unknown>;
      const id = current.id;
      const books = normalizeBooks(current.books);
      if (id === "recommended") home.recommended = books;
      else if (id === "new" || id === "newBooks") home.newBooks = books;
      else if (isBookSection(id)) home.categories[id] = books;
    });
  }

  return home;
}

export function getSafeExternalUrl(value?: string): string | undefined {
  if (!value) return undefined;

  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : undefined;
  } catch {
    return undefined;
  }
}
